import React, { useState, useEffect } from 'react';
import { Pencil, Loader2 } from 'lucide-react';
import { beneficiariesAPI, vendorAPI } from '../services/api';
import VendorAssignmentSelect from './VendorAssignmentSelect';

const EditBeneficiaryModal = ({ beneficiary, onClose, onUpdate, role }) => {
  // Safety check: if beneficiary is null/undefined, don't render
  if (!beneficiary) {
    return null;
  }

  const [name, setName] = useState(beneficiary.name || '');
  const [idNumber, setIdNumber] = useState(beneficiary.id_number || '');
  const [phone, setPhone] = useState(beneficiary.phone || '');
  const [couponCount, setCouponCount] = useState(beneficiary.coupon_count || 1);
  const [couponValue, setCouponValue] = useState(beneficiary.coupon_value || 0);
  const [assignedVendors, setAssignedVendors] = useState(() => {
    try {
      if (!beneficiary.assigned_vendors) return [];
      if (Array.isArray(beneficiary.assigned_vendors)) return beneficiary.assigned_vendors;
      if (typeof beneficiary.assigned_vendors === 'string') {
        const parsed = JSON.parse(beneficiary.assigned_vendors);
        return Array.isArray(parsed) ? parsed : [];
      }
      return [];
    } catch (e) {
      console.error('Error parsing assigned_vendors:', e);
      return [];
    }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [vendorList, setVendorList] = useState(['ALL VENDORS']);

  const isSuperAdmin = role === 'superadmin';

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await vendorAPI.getAll();
        setVendorList(response.data || ['ALL VENDORS']);
      } catch (error) {
        console.error('Error fetching vendors:', error);
        setVendorList(['ALL VENDORS']);
      }
    };
    
    fetchVendors();
    
    // Refresh vendors periodically to catch newly added vendors
    const vendorRefreshInterval = setInterval(() => {
      fetchVendors();
    }, 30000); // Refresh every 30 seconds
    
    // Refresh vendors when window regains focus (user returns from another page)
    const handleFocus = () => {
      fetchVendors();
    };
    
    // Refresh vendors when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchVendors();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearInterval(vendorRefreshInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);
    
    const newCouponCount = parseInt(couponCount, 10);
    
    if (newCouponCount < beneficiary.redeemed_count) {
      setError(`لا يمكن تقليل إجمالي القسائم إلى أقل من العدد المسترد (${beneficiary.redeemed_count}).`);
      setIsSaving(false);
      return;
    }
    
    if (assignedVendors.length === 0) {
      setError("يرجى تعيين بائع واحد على الأقل أو اختيار 'جميع البائعين'.");
      setIsSaving(false);
      return;
    }

    try {
      const updateData = {
        name: name.trim(),
        phone: phone.trim(),
        coupon_count: newCouponCount,
        coupon_value: parseFloat(couponValue) || 0,
        assigned_vendors: assignedVendors,
      };
      
      // Only superadmin can update id_number
      if (isSuperAdmin && idNumber.trim() !== beneficiary.id_number) {
        updateData.id_number = idNumber.trim();
      }
      
      await beneficiariesAPI.update(beneficiary.id, updateData);
      
      onUpdate();
      onClose();
    } catch (err) {
      console.error("Error updating beneficiary:", err);
      setError(err.response?.data?.error || "فشل في تحديث بيانات المستفيد.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-indigo-700 flex items-center">
            <Pencil className="w-6 h-6 mr-2" /> تعديل المستفيد: {beneficiary.name || 'غير معروف'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
            disabled={isSaving}
            type="button"
          >
            ×
          </button>
        </div>
        
        {error && (
          <div className="p-3 mb-4 bg-red-100 text-red-700 rounded-lg text-sm font-medium">{error}</div>
        )}

        <form onSubmit={handleUpdate} className="space-y-4">
          {/* ID Number - Editable for superadmin, read-only for others */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              رقم الهوية الوطنية
              {isSuperAdmin && <span className="text-xs text-blue-600 ml-2">(قابل للتعديل)</span>}
            </label>
            <input 
              type="text" 
              value={idNumber} 
              onChange={(e) => setIdNumber(e.target.value)}
              readOnly={!isSuperAdmin}
              className={`mt-1 w-full p-3 border rounded-lg font-mono ${
                isSuperAdmin 
                  ? 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500' 
                  : 'border-gray-200 bg-gray-100'
              }`}
              required
            />
          </div>
          
          {/* Editable Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700">الاسم الكامل</label>
            <input 
              type="text" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
              required 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">رقم الهاتف</label>
            <input 
              type="tel" 
              value={phone} 
              onChange={(e) => setPhone(e.target.value)} 
              className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
              required 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">عدد القسائم (مسترد: {beneficiary.redeemed_count})</label>
            <input 
              type="number" 
              value={couponCount} 
              min={beneficiary.redeemed_count} 
              onChange={(e) => setCouponCount(Math.max(beneficiary.redeemed_count, parseInt(e.target.value) || beneficiary.redeemed_count))} 
              className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
              required 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">قيمة القسيمة</label>
            <input 
              type="number" 
              step="0.01"
              value={couponValue} 
              min="0"
              onChange={(e) => setCouponValue(parseFloat(e.target.value) || 0)} 
              className="mt-1 w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
            />
          </div>
          
          {/* Vendor Assignment */}
          <div>
            <label className="block text-sm font-medium text-gray-700">البائعون المعينون</label>
            <VendorAssignmentSelect 
              selectedVendors={assignedVendors} 
              onChange={setAssignedVendors}
              vendorList={vendorList}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
              disabled={isSaving}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center disabled:opacity-50"
            >
              {isSaving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Pencil className="w-5 h-5 mr-2" />}
              {isSaving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBeneficiaryModal;
